import { useCallback, useMemo } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, DECIMAL_PROPS, NumberInput, Select, YesNoRadioButton } from "components";
import SectionTitle from "pages/clients/view/components/section-title";
import { CreditOptionsResponse, CreditResponse, ExpenseType } from "types";
import { AppUtils } from "utils";

interface CreditRequestExpensesProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  isBankCredit?: boolean;
}

interface ExpenseObject {
  creditRequestId?: number;
  expenseType?: ExpenseType;
  amount?: number;
  anotherExpense?: boolean;
}

const tempExpenseObject = {
  creditRequestId: undefined,
  expenseType: undefined,
  amount: undefined,
  anotherExpense: undefined,
} as ExpenseObject;

const propertyName = "expenses";

const CreditRequestExpenses = ({
  credit,
  form,
  options,
  readOnly = false,
  isBankCredit = false,
}: CreditRequestExpensesProps) => {
  const { t } = useTranslation();

  const updateCredit = useCallback(
    async (array: any) => {
      const tempArray: ExpenseObject[] = [];
      Object.keys(array).forEach((key) => {
        const item: ExpenseObject = { ...array[key] };
        const isNotEmpty = item.expenseType || item.amount;
        if (isNotEmpty) {
          item.creditRequestId = credit.id;
          tempArray.push(item);
        }
      });
      form.setFieldValue(propertyName, tempArray);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [credit.id],
  );

  const expenseForm = useForm({
    mode: "uncontrolled",
    initialValues:
      credit[propertyName] && credit[propertyName].length > 0
        ? credit[propertyName].map((el) => ({ ...el }))
        : [tempExpenseObject],
    onValuesChange(values, _previous) {
      if (values[values.length - 1] !== tempExpenseObject) updateCredit(values);
    },
  });

  const formKeys = useMemo(() => {
    const form = expenseForm.getValues();
    return Object.keys(form);
  }, [expenseForm]);

  return (
    <Box mt={"md"} mb={"md"}>
      <SectionTitle label={t("credit.expenses.title")} isBankCredit={isBankCredit} />

      <Box pr={"md"}>
        {formKeys.map((key: any, index: number) => (
          <SimpleGrid key={key} cols={4} mt={"md"}>
            <Select
              label={t("credit.expenseType.label")}
              data={options["expenseType"].map((el) => ({
                value: el,
                label: t(`credit.expenseType.options.${el}`),
              }))}
              key={expenseForm.key(`${index}.expenseType`)}
              {...expenseForm.getInputProps(`${index}.expenseType`)}
              clearable={true}
              disabled={readOnly}
            />
            <NumberInput
              label={t("credit.expenseType.amount.label")}
              key={expenseForm.key(`${index}.amount`)}
              {...expenseForm.getInputProps(`${index}.amount`)}
              min={0}
              {...DECIMAL_PROPS}
              disabled={readOnly}
            />
            <YesNoRadioButton
              label={t(`credit.expenseType.anotherExpense.label`)}
              name={`${index}.anotherExpense`}
              form={expenseForm}
              onChange={(checked) => {
                if (checked) {
                  AppUtils.addNewObject(expenseForm, tempExpenseObject);
                } else if (index < formKeys.length - 1) {
                  AppUtils.removeObject(expenseForm, index + 1, tempExpenseObject);
                }
              }}
              disabled={readOnly}
            />
            {formKeys.length > 1 && !readOnly && (
              <Box style={{ alignSelf: "flex-end" }}>
                <Button
                  variant={"transparent"}
                  size={"compact-sm"}
                  color={"var(--mantine-color-error)"}
                  mb={6}
                  onClick={() => AppUtils.removeObject(expenseForm, index, tempExpenseObject)}
                >
                  <IconTrash size={20} />
                </Button>
              </Box>
            )}
          </SimpleGrid>
        ))}
      </Box>
    </Box>
  );
};

export default CreditRequestExpenses;
